/**
 * URL-to-Team Mapping for Sentry Error Tagging
 *
 * Maps URL patterns to team ownership based on CODEOWNERS.
 * Used to automatically tag Sentry errors from non-React (HAML) pages with the appropriate team.
 */

import { type Team } from 'hb-react/types/error';

interface UrlPatternMapping {
  description?: string;
  pattern: RegExp;
  team: Team;
}

/**
 * Safely extracts pathname from a URL string
 *
 * @param url - Full URL or pathname
 * @returns Pathname string or null if invalid
 */
function getPathnameFromUrl(url: string): string | null {
  try {
    // Handle both full URLs and pathnames
    return url.startsWith('http') ? new URL(url).pathname : url;
  } catch {
    // Invalid URL
    return null;
  }
}

/**
 * URL pattern mappings ordered by specificity (most specific first)
 * Based on .github/CODEOWNERS file
 */
const URL_TO_TEAM_PATTERNS: Array<UrlPatternMapping> = [
  // ============================================
  // BACKOFFICE - Admin XP Team
  // ============================================
  {
    pattern: /\/backoffice\/.*\/communications\/emailings/,
    team: 'squad-admin-xp',
    description: 'Email campaigns and emailings',
  },
  {
    pattern: /\/backoffice\/.*\/communications\/email_templates/,
    team: 'squad-admin-xp',
    description: 'Email templates',
  },
  {
    pattern: /\/backoffice\/.*\/builder/,
    team: 'squad-admin-xp',
    description: 'Page builder',
  },
  {
    pattern: /\/backoffice\/.*\/page_customizables/,
    team: 'squad-admin-xp',
    description: 'Page customization and branding',
  },
  {
    pattern: /\/backoffice\/.*\/dynamic_components/,
    team: 'squad-admin-xp',
    description: 'Dynamic components',
  },
  {
    pattern: /\/backoffice\/.*\/user_lists/,
    team: 'squad-admin-xp',
    description: 'User lists management',
  },
  {
    pattern: /\/backoffice\/.*\/moderations\/reported_content/,
    team: 'squad-admin-xp',
    description: 'Content moderation',
  },
  {
    pattern: /\/backoffice\/.*\/roles/,
    team: 'squad-admin-xp',
    description: 'Roles and permissions',
  },
  {
    pattern: /\/backoffice\/.*\/analytics/,
    team: 'squad-admin-xp',
    description: 'Analytics',
  },
  {
    pattern: /\/backoffice\/search_users/,
    team: 'squad-admin-xp',
    description: 'User search',
  },
  {
    pattern: /\/backoffice\/.*\/admins\/notifications/,
    team: 'squad-admin-xp',
    description: 'Admin notifications',
  },

  // ============================================
  // BACKOFFICE - Evolution Team
  // ============================================
  {
    pattern: /\/backoffice\/.*\/events/,
    team: 'squad-evolution-frontend',
    description: 'Events management',
  },
  {
    pattern: /\/backoffice\/.*\/donations/,
    team: 'squad-evolution-frontend',
    description: 'Donations',
  },
  {
    pattern: /\/backoffice\/.*\/communications\/news/,
    team: 'squad-evolution-frontend',
    description: 'News articles',
  },
  {
    pattern: /\/backoffice\/.*\/forums/,
    team: 'squad-evolution-frontend',
    description: 'Forums',
  },
  {
    pattern: /\/backoffice\/.*\/jobs/,
    team: 'squad-evolution-frontend',
    description: 'Job board',
  },
  {
    pattern: /\/backoffice\/.*\/journeys/,
    team: 'squad-evolution-frontend',
    description: 'User journeys',
  },
  {
    pattern: /\/backoffice\/.*\/media_center/,
    team: 'squad-evolution-frontend',
    description: 'Media center',
  },
  {
    pattern: /\/backoffice\/.*\/memberships/,
    team: 'squad-evolution-frontend',
    description: 'Memberships',
  },
  {
    pattern: /\/backoffice\/.*\/mentorings/,
    team: 'squad-evolution-frontend',
    description: 'Mentoring',
  },
  {
    pattern: /\/backoffice\/.*\/payments/,
    team: 'squad-evolution-frontend',
    description: 'Payments',
  },
  {
    pattern: /\/backoffice\/.*\/portfolio/,
    team: 'squad-evolution-frontend',
    description: 'Portfolio',
  },
  {
    pattern: /\/backoffice\/.*\/promo_codes/,
    team: 'squad-evolution-frontend',
    description: 'Promo codes',
  },
  {
    pattern: /\/backoffice\/.*\/surveys/,
    team: 'squad-evolution-frontend',
    description: 'Surveys',
  },
  {
    pattern: /\/backoffice\/.*\/ventures/,
    team: 'squad-evolution-frontend',
    description: 'Ventures',
  },
  {
    pattern: /\/backoffice\/.*\/polls/,
    team: 'squad-evolution-frontend',
    description: 'Polls',
  },

  // ============================================
  // BACKOFFICE - UJ Team
  // ============================================
  {
    pattern: /\/backoffice\/.*\/topics/,
    team: 'squad-uj',
    description: 'Topics management',
  },
  {
    pattern: /\/backoffice\/.*\/login_activities/,
    team: 'squad-uj',
    description: 'Login activities',
  },

  // ============================================
  // FRONTOFFICE - Evolution Team
  // ============================================
  {
    pattern: /\/(events|event)/,
    team: 'squad-evolution-frontend',
    description: 'Events',
  },
  {
    pattern: /\/(donations|donation)/,
    team: 'squad-evolution-frontend',
    description: 'Donations',
  },
  {
    pattern: /\/(news|article)/,
    team: 'squad-evolution-frontend',
    description: 'News',
  },
  {
    pattern: /\/(forums|forum)/,
    team: 'squad-evolution-frontend',
    description: 'Forums',
  },
  {
    pattern: /\/(jobs|job)/,
    team: 'squad-evolution-frontend',
    description: 'Jobs',
  },
  {
    pattern: /\/(journeys|journey)/,
    team: 'squad-evolution-frontend',
    description: 'Journeys',
  },
  {
    pattern: /\/media_center/,
    team: 'squad-evolution-frontend',
    description: 'Media center',
  },
  {
    pattern: /\/(mentorings|mentoring)/,
    team: 'squad-evolution-frontend',
    description: 'Mentoring',
  },
  {
    pattern: /\/(memberships|membership)/,
    team: 'squad-evolution-frontend',
    description: 'Memberships',
  },
  {
    pattern: /\/(portfolio|portfolios)/,
    team: 'squad-evolution-frontend',
    description: 'Portfolio',
  },
  {
    pattern: /\/(ventures|venture)/,
    team: 'squad-evolution-frontend',
    description: 'Ventures',
  },
  {
    pattern: /\/polls/,
    team: 'squad-evolution-frontend',
    description: 'Polls',
  },

  // ============================================
  // FRONTOFFICE - UJ Team
  // ============================================
  {
    pattern: /\/(live_feed|feed)/,
    team: 'squad-uj',
    description: 'Live feed',
  },
  {
    pattern: /\/(profile|user|users|member|members)/,
    team: 'squad-uj',
    description: 'User profiles',
  },
  {
    pattern: /\/(directory|people)/,
    team: 'squad-uj',
    description: 'People directory',
  },
  {
    pattern: /\/search/,
    team: 'squad-uj',
    description: 'Search',
  },
  {
    pattern: /\/notifications/,
    team: 'squad-uj',
    description: 'Notifications',
  },
  {
    pattern: /\/settings/,
    team: 'squad-uj',
    description: 'User settings',
  },
  {
    pattern: /\/(topics|topic)/,
    team: 'squad-uj',
    description: 'Topics',
  },
  {
    pattern: /\/login_activities/,
    team: 'squad-uj',
    description: 'Login activities',
  },
  {
    pattern: /\/ai\/(search|recommendations|summaries|text_enhancements)/,
    team: 'squad-uj',
    description: 'AI features',
  },
  {
    pattern: /\/content_recommendations/,
    team: 'squad-uj',
    description: 'Content recommendations',
  },
  {
    pattern: /\/badges/,
    team: 'squad-uj',
    description: 'Badges',
  },
  {
    pattern: /\/bookmarks/,
    team: 'squad-uj',
    description: 'Bookmarks',
  },
  {
    pattern: /\/celebrations/,
    team: 'squad-uj',
    description: 'Celebrations',
  },
  {
    pattern: /\/comments/,
    team: 'squad-uj',
    description: 'Comments',
  },
];

/**
 * Determines the team responsible for a given URL based on pattern matching
 *
 * @param url - Full URL or pathname to match
 * @returns Team identifier or null if no match found
 *
 * @example
 * getTeamFromUrl('/backoffice/networks/123/events') // 'squad-evolution-frontend'
 * getTeamFromUrl('/live_feed') // 'squad-uj'
 * getTeamFromUrl('/backoffice/networks/123/communications/emailings') // 'squad-admin-xp'
 */
export function getTeamFromUrl(url: string): Team | null {
  const pathname = getPathnameFromUrl(url);
  if (!pathname) {
    return null;
  }

  // Iterate through patterns in order (most specific first)
  for (const mapping of URL_TO_TEAM_PATTERNS) {
    if (mapping.pattern.test(pathname)) {
      return mapping.team;
    }
  }

  return null;
}

/**
 * Gets team and module information from URL for more detailed Sentry tagging
 *
 * @param url - Full URL or pathname to match
 * @returns Object with team and optional module name, or null if no match
 *
 * @example
 * getTeamAndModuleFromUrl('/live_feed') // { team: 'squad-uj', module: 'live_feed' }
 * getTeamAndModuleFromUrl('/backoffice/networks/123/events') // { team: 'squad-evolution-frontend', module: 'events' }
 */
export function getTeamAndModuleFromUrl(
  url: string,
): { module?: string; team: Team } | null {
  const team = getTeamFromUrl(url);
  if (!team) {
    return null;
  }

  // Extract module name from URL (pathname is already validated in getTeamFromUrl)
  const pathname = getPathnameFromUrl(url);
  if (!pathname) {
    return null;
  }

  // Try to extract a meaningful module name
  const pathParts = pathname.split('/').filter(Boolean);

  // For backoffice URLs, module is typically after 'backoffice/networks/{id}/'
  if (pathname.includes('/backoffice/')) {
    const boIndex = pathParts.indexOf('backoffice');
    // Skip 'networks' and network ID
    const moduleIndex =
      boIndex + (pathParts[boIndex + 1] === 'networks' ? 3 : 1);
    const boModule = pathParts[moduleIndex];
    return { team, module: boModule };
  }

  // For frontoffice URLs, first path segment is often the module
  const foModule = pathParts[0];
  return { team, module: foModule };
}
